import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { newsApi } from "../api/news.api";
import { nextLastSeenNewsAt } from "./has-unread-news";
import type { User } from "@/entities/user";

export function useMarkNewsSeen() {
  const { getToken, user } = useKindeAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const token = await getToken();
      if (!token) {
        throw new Error("Unauthorized");
      }
      return newsApi.markSeen(token);
    },
    onMutate: async () => {
      const userId = user?.id;
      if (!userId) return;
      await queryClient.cancelQueries({ queryKey: ["user", userId] });
      const previous = queryClient.getQueryData<User>(["user", userId]);
      const lastSeenNewsAt = nextLastSeenNewsAt();
      queryClient.setQueryData<User>(["user", userId], (current) =>
        current ? { ...current, lastSeenNewsAt } : current
      );
      return { previous, userId };
    },
    onError: (_error, _variables, context) => {
      if (!context?.userId) return;
      queryClient.setQueryData(["user", context.userId], context.previous);
    },
    onSuccess: (result) => {
      const userId = user?.id;
      if (!userId) return;
      queryClient.setQueryData<User>(["user", userId], (current) =>
        current ? { ...current, lastSeenNewsAt: result.lastSeenNewsAt } : current
      );
    },
  });
}
